import { useCallback, useEffect, useState } from "react";
import { BoundingBox, ResizeHandle } from "../types";
import { applyDrag, MIN_BBOX_SIZE } from "../utils/bboxDrag";

interface DragState {
	startPos: { x: number; y: number };
	startBbox: BoundingBox;
	handle: ResizeHandle;
	canvasElement: Element;
}

/**
 * Move/resize for a single standalone rectangle (the free-drawn embed area), as opposed
 * to `useBboxManipulation`, which addresses one of a list of scene objects by id. Both
 * run the same gesture math from `applyDrag`.
 */
export const useRectManipulation = (
	bbox: BoundingBox | null,
	canvasSize: { width: number; height: number },
	onUpdate: (bbox: BoundingBox) => void,
	minSize: number = MIN_BBOX_SIZE,
) => {
	const [dragState, setDragState] = useState<DragState | null>(null);

	const handleMouseDown = useCallback(
		(e: React.MouseEvent, handle: ResizeHandle = null) => {
			e.stopPropagation();
			if (!bbox) return;

			const canvasElement = (e.currentTarget as HTMLElement).closest("[data-canvas-container]");
			if (!canvasElement) return;

			const rect = canvasElement.getBoundingClientRect();
			setDragState({
				startPos: { x: e.clientX - rect.left, y: e.clientY - rect.top },
				startBbox: { ...bbox },
				handle,
				canvasElement,
			});
		},
		[bbox],
	);

	useEffect(() => {
		if (!dragState) return;

		const { startPos, startBbox, handle, canvasElement } = dragState;

		const handleMouseMove = (e: MouseEvent) => {
			const rect = canvasElement.getBoundingClientRect();
			const dx = ((e.clientX - rect.left - startPos.x) / canvasSize.width) * 100;
			const dy = ((e.clientY - rect.top - startPos.y) / canvasSize.height) * 100;

			onUpdate(applyDrag(startBbox, dx, dy, handle, minSize));
		};

		const handleMouseUp = () => setDragState(null);

		window.addEventListener("mousemove", handleMouseMove);
		window.addEventListener("mouseup", handleMouseUp);
		return () => {
			window.removeEventListener("mousemove", handleMouseMove);
			window.removeEventListener("mouseup", handleMouseUp);
		};
	}, [dragState, canvasSize, onUpdate, minSize]);

	return { handleMouseDown, isDragging: dragState !== null };
};
